import { usePushConfig } from '../hooks/usePushConfig';
import { usePushSubscription, type PushTopic } from '../hooks/usePushSubscription';
import { Button } from './Button';
import styles from './PushOptIn.module.css';

interface PushOptInProps {
  topic: PushTopic;
  /** Button text while off, e.g. "Notify me on UP/DOWN calls". */
  offLabel: string;
  /** Status text while on, e.g. "UP/DOWN call alerts on". */
  onLabel: string;
}

/**
 * Opt-in for one push notification topic. Renders nothing if push isn't
 * configured on this deploy, or if the browser/permission state makes it
 * unavailable.
 */
export function PushOptIn(props: PushOptInProps) {
  const { data } = usePushConfig();
  if (!data?.enabled || !data.publicKey) return null;
  return <PushOptInInner vapidPublicKey={data.publicKey} {...props} />;
}

function PushOptInInner({
  vapidPublicKey,
  topic,
  offLabel,
  onLabel,
}: PushOptInProps & { vapidPublicKey: string }) {
  const { state, subscribe, unsubscribe } = usePushSubscription(vapidPublicKey, topic);

  if (state === 'unsupported' || state === 'pending') return null;

  if (state === 'denied') {
    return (
      <p className={styles.denied}>
        Notifications are blocked in your browser settings — you can still check back here
        any time.
      </p>
    );
  }

  if (state === 'subscribed') {
    return (
      <div className={styles.subscribed}>
        <p className={styles.subscribedLabel}>✓ {onLabel}</p>
        <button type="button" className={styles.turnOff} onClick={unsubscribe}>
          Turn off
        </button>
      </div>
    );
  }

  return (
    <Button type="button" onClick={subscribe}>
      {offLabel}
    </Button>
  );
}
